import type { ReactNode } from 'react';
import React, { forwardRef, useMemo } from 'react';

import { Autocomplete, Box, TextField } from '@mui/material';

import { InputError, InputLabel } from 'shared/ui-kit';

import { textFilledStylesVariant } from '../MuiBaseTextFiled/utils/text-filled-styles-variant';

import type { SxProps, Theme } from '@mui/material';
import type { MuiBaseTextFiledInputVariant } from '@shared/ui-kit/inputs/MuiBaseTextFiled/MuiBaseTextFiled';

type InputVariant = 'text' | 'search';
type MuiBaseAutocompleteTextFiledPropsType = {
  labelId?: string;
  inputId?: string;
  fieldValue: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  isFullWidth?: boolean;
  type?: string;
  isError?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: string;
  errorMessage?: string;
  size?: 'small' | 'medium';
  inputVariant?: InputVariant;
  isToUpperCase?: boolean;
  isShowHint?: boolean;
  maxHintValue?: number;
  autoCompleteOptions: readonly string[];
  onChangeTextField?: (val: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeSelectAutocomplete?: (event: React.SyntheticEvent, value: string) => void;
  startIcon?: ReactNode;
};

export const MuiBaseAutocompleteTextFiled = forwardRef<
  HTMLHeadingElement,
  MuiBaseAutocompleteTextFiledPropsType
>(
  (
    {
      labelId,
      inputId,
      fieldValue,
      placeholder,
      label,
      isShowHint,
      maxHintValue,
      isRequired,
      isToUpperCase,
      errorMessage,
      isError,
      autoCompleteOptions,
      size,
      isDisabled,
      helperText,
      onChangeTextField,
      onChangeSelectAutocomplete,
      isFullWidth,
      inputVariant,
      startIcon,
    },
    ref,
  ) => {
    const inputStyle = useMemo(() => {
      return textFilledStylesVariant({
        inputVariant: inputVariant as MuiBaseTextFiledInputVariant,
      });
    }, [inputVariant]);

    return (
      <Autocomplete
        freeSolo
        sx={{ '& .MuiOutlinedInput-root .MuiAutocomplete-input': { padding: 0 } }}
        disableClearable
        value={fieldValue}
        options={autoCompleteOptions}
        disabled={isDisabled}
        onChange={onChangeSelectAutocomplete}
        renderInput={(params) => {
          return (
            <Box width="100%" sx={{ ...(inputStyle as SxProps<Theme>) }}>
              <InputLabel
                id={labelId}
                label={isToUpperCase ? label?.toUpperCase() : label}
                isShowHint={isShowHint}
                isRequired={isRequired}
                currentHintValue={fieldValue?.length}
                maxHintValue={maxHintValue}
              />
              <TextField
                {...params}
                id={inputId}
                variant="outlined"
                value={fieldValue}
                placeholder={placeholder}
                onChange={onChangeTextField}
                inputRef={ref}
                helperText={helperText}
                fullWidth={isFullWidth}
                required={isRequired}
                error={isError}
                size={size}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: startIcon,
                }}
              />
              {errorMessage ? <InputError isShow={isError} message={errorMessage} /> : <></>}
            </Box>
          );
        }}
      />
    );
  },
);
